import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { MagnifyingGlass, Clock, ArrowRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API}/search`, {
        params: { q: searchQuery, limit: 30 }
      });
      setResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
      performSearch(query.trim());
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const regionLabels = {
    ireland: "Ireland",
    uk: "UK",
    usa: "USA",
    china: "China",
    india: "India",
    brazil: "Brazil",
    global: "Global"
  };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="Search" description="Search Farmwatch for farming news, market prices, land listings, jobs and marketplace items." url="/search" />
      <Header />
      
      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-8">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-[#33302E] mb-6">Search Articles</h1>
          
          <form onSubmit={handleSubmit} className="relative">
            <MagnifyingGlass
              size={24}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6661]"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for farming news, commodities, markets..."
              data-testid="search-input"
              className="w-full pl-12 pr-32 py-4 border border-[#E0D4C8] bg-white font-sans text-lg focus:outline-none focus:border-[#0F766E]"
            />
            <button
              type="submit"
              data-testid="search-submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-[#111827] text-white font-sans hover:bg-[#0F766E] transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E0D4C8] p-6 animate-pulse">
                <div className="h-4 bg-[#E5E7EB] w-1/4 mb-3"></div>
                <div className="h-6 bg-[#E5E7EB] w-3/4 mb-2"></div>
                <div className="h-4 bg-[#E5E7EB] w-full"></div>
              </div>
            ))}
          </div>
        ) : searched ? (
          <>
            <p className="text-sm text-[#6B6661] mb-6">
              {results.length} result{results.length !== 1 ? "s" : ""} for "{searchParams.get("q")}"
            </p>

            {results.length === 0 ? (
              <div className="text-center py-12 border border-[#E0D4C8] bg-white">
                <MagnifyingGlass size={48} className="mx-auto mb-4 text-[#E5E7EB]" />
                <h3 className="font-serif text-xl text-[#33302E] mb-2">No results found</h3>
                <p className="text-[#6B6661]">Try different keywords or browse our latest news</p>
                <Link
                  to="/"
                  className="inline-block mt-4 px-6 py-2 bg-[#111827] text-white font-sans hover:bg-[#0F766E] transition-colors"
                >
                  Browse News
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {results.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="block bg-white border border-[#E0D4C8] p-6 hover:border-[#0F766E] transition-colors group"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-[#0F766E] text-white text-[10px] font-mono uppercase tracking-wider">
                        {regionLabels[article.region] || article.region}
                      </span>
                      <span className="px-2 py-0.5 bg-[#E5E7EB] text-[#6B6661] text-[10px] font-mono uppercase tracking-wider">
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#6B6661] font-mono ml-auto">
                        <Clock size={12} />
                        {formatDate(article.published_at)}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl text-[#33302E] mb-2 group-hover:text-[#0F766E] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-[#6B6661] line-clamp-2">{article.summary}</p>
                    <span className="inline-flex items-center gap-2 text-[#0F766E] font-sans text-sm mt-3 group-hover:gap-3 transition-all">
                      Read article <ArrowRight size={16} />
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-[#6B6661]">
            <MagnifyingGlass size={48} className="mx-auto mb-4 text-[#E5E7EB]" />
            <p>Enter a search term to find articles</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
